import React, { useEffect, useState } from "react";
import { Plus, X, Trash2, Edit2, Save, Shield, KeyRound } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const blank = { email: "", name: "", password: "" };

export const AdminAdminsTab = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null); // user object being edited
  const [editForm, setEditForm] = useState(blank);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/admins");
    setItems(data || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await api.post("/admin/admins", form);
      setForm(blank); setShowCreate(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  const startEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email, password: "" });
    setError("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const payload = {};
      if (editForm.name && editForm.name !== editing.name) payload.name = editForm.name;
      if (editForm.email && editForm.email !== editing.email) payload.email = editForm.email;
      if (editForm.password) payload.password = editForm.password;
      if (Object.keys(payload).length === 0) {
        setEditing(null);
        return;
      }
      await api.patch(`/admin/admins/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally { setSubmitting(false); }
  };

  const remove = async (u) => {
    if (u.id === me.id) {
      window.alert("You can't delete your own admin account.");
      return;
    }
    if (!window.confirm(`Delete admin "${u.email}"? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/admins/${u.id}`);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="admin-admins-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow inline-flex items-center gap-2">
          <Shield size={14} /> / ADMIN ACCOUNTS
        </p>
        <button
          onClick={() => { setShowCreate((v) => !v); setForm(blank); setError(""); }}
          className="bg-kult-pink text-black font-display text-base px-4 py-2 hover:bg-kult-yellow inline-flex items-center gap-2"
          data-testid="admins-toggle-create"
        >
          {showCreate ? <X size={16} /> : <Plus size={16} />} {showCreate ? "CLOSE" : "NEW ADMIN"}
        </button>
      </div>

      <p className="text-kult-muted text-sm mb-6">
        Admin accounts have full backoffice access. Grant only to trusted teammates. You cannot delete your own account, and the last remaining admin can't be removed.
      </p>

      {showCreate && (
        <form
          onSubmit={submit}
          className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid="admin-create-admin-form"
        >
          <h3 className="md:col-span-3 font-display text-2xl">NEW ADMIN</h3>
          <Field label="NAME" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="new-admin-name" />
          <Field label="EMAIL" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="new-admin-email" />
          <Field label="PASSWORD (min 8)" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="new-admin-password" />
          {error && <p className="md:col-span-3 font-mono text-xs text-kult-pink" data-testid="new-admin-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="md:col-span-3 bg-kult-pink text-black font-display text-xl py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="new-admin-submit"
          >
            <Save size={18} /> {submitting ? "CREATING..." : "CREATE ADMIN"}
          </button>
        </form>
      )}

      <div className="border border-kult-border" data-testid="admins-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-3">NAME</span>
          <span className="col-span-4">EMAIL</span>
          <span className="col-span-3">CREATED</span>
          <span className="col-span-2 text-right">ACTIONS</span>
        </div>
        {items.length === 0 && (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No admins yet.</p>
        )}
        {items.map((u) => (
          <React.Fragment key={u.id}>
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border items-center" data-testid={`admin-row-${u.id}`}>
              <span className="col-span-3 font-display text-base">
                {u.name}{u.id === me.id && <span className="ml-2 font-mono text-[9px] tracking-[0.3em] text-kult-yellow">YOU</span>}
              </span>
              <span className="col-span-4 font-mono text-xs text-white truncate" title={u.email}>{u.email}</span>
              <span className="col-span-3 font-mono text-[11px] text-kult-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</span>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <button onClick={() => startEdit(u)} className="p-1.5 border border-kult-border hover:border-kult-pink" data-testid={`admin-edit-${u.id}`} title="Edit"><Edit2 size={14} /></button>
                <button
                  onClick={() => remove(u)}
                  disabled={u.id === me.id}
                  className={`p-1.5 border border-kult-border ${u.id === me.id ? "opacity-30 cursor-not-allowed" : "hover:border-kult-pink text-kult-pink"}`}
                  data-testid={`admin-delete-${u.id}`}
                  title={u.id === me.id ? "Can't delete yourself" : "Delete"}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {editing && editing.id === u.id && (
              <form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-4 border-b border-kult-border bg-kult-black2" data-testid={`admin-edit-form-${u.id}`}>
                <Field className="md:col-span-3" label="NAME" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} testId={`admin-edit-name-${u.id}`} />
                <Field className="md:col-span-4" label="EMAIL" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} testId={`admin-edit-email-${u.id}`} />
                <Field className="md:col-span-3" label="NEW PASSWORD (blank = keep)" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} testId={`admin-edit-password-${u.id}`} icon={<KeyRound size={12} />} />
                <div className="md:col-span-2 flex items-end gap-2">
                  <button type="submit" disabled={submitting} className="flex-1 bg-kult-pink text-black font-mono text-[11px] tracking-[0.25em] py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center justify-center gap-1" data-testid={`admin-edit-save-${u.id}`}>
                    <Save size={12} /> SAVE
                  </button>
                  <button type="button" onClick={() => setEditing(null)} className="px-3 py-3 border border-kult-border text-kult-muted hover:text-white" data-testid={`admin-edit-cancel-${u.id}`}>
                    <X size={14} />
                  </button>
                </div>
                {error && <p className="md:col-span-12 font-mono text-xs text-kult-pink" data-testid={`admin-edit-error-${u.id}`}>{error}</p>}
              </form>
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

function Field({ label, testId, icon, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2 inline-flex items-center gap-1">
        {icon}{label}
      </span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}
